import React from 'react';
import _ from 'lodash';
import Helmet from 'react-helmet';

import { extractErrorMessage } from '../utilities/commonUtil';
import _masterSlideLoader from '../utilities/masterSlideLoader';
import { validationByJoi } from '../utilities/commonValidation';
import { FieldScheme } from '../utilities/JoiSchemes';
import {
  isTargetRoleId,
  isPrimaryCompanyRoleId,
} from '../utilities/roleUtility';
import { messageUtility } from '../utilities/messageUtility';
import {
  USER_ROLES,
  HEADER_CONSTANTS,
  CONTRACT_FLAG,
} from '../utilities/constants';
import dialogLoader from '../utilities/dialogLoader';

import { MasterPageHeader } from '../components/PageHeader';
import { CloseEditView } from '../components/CloseEditView';
import { FieldNaviList } from '../components/list/FieldNaviList';
import { Confirmation, Information } from '../components/modals/common';
import { JvCompanyDropdown } from '../components/dropdowns/single/JvCompany';
import { JvCompanyBranchDropdown } from '../components/dropdowns/single/JvCompanyBranch';
import { Description } from '../components/Description';
import { createFormEventManager, Form, FormItem } from '../components/FormBase';
import {
  FormList,
  FormListSubHeader,
  FormListItem,
  FormElement,
  FormElementContainer,
  FormElementContainerItem,
  FormElementContainerItemLabel,
} from '../components/CommonFormComponents';

import LocalStorageService from '../services/utilities/LocalStorageService';
import SessionStorageService from '../services/utilities/SessionStorageService';
import CompaniesFieldsService from '../services/CompaniesFields';
import FieldCcusApi from '../services/FieldCcus';
import { jvCompanyList } from '../services/FieldTree';

const JV_ACTION = {
  delete: { id: 0, name: '削除する' },
  regist: { id: 1, name: '追加する' },
};

/**
 * @description 新規現場登録、新規現場登録申請画面
 */
export class FieldJvSetting extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      fieldId: null,
      jvFlag: true,
      validate: [], // バリデーションメッセージを格納
      params: {}, // 現場情報を保持
      targetJv: {},
      editingFlag: false,
      jvsParams: [], // FormBase用JV情報を保持
      mainCompany: {},
      disabledCooperationCcus: true,
      informationModal: {
        // エラーが発生した時に表示するダイアログ
        show: false,
        message: '',
      },
    };

    // slideLoaderからの情報を取得
    if (props.view) {
      this.state.fieldId = props.view.state.id;
    } else if (props.match) {
      this.state.fieldId = props.match.params.id;
    }

    this.events = {
      modal: {
        openCloseEditModal: this.openCloseEditModal.bind(this),
      },
    };
    this.setEditingFlag = this.setEditingFlag.bind(this);
    // Form で使うEventManagerを取得
    this.FormEventMg = createFormEventManager();
    this.FormEventMg.addEventListener('change_value', (id, params) => {
      if (!Object.keys(params).length) {
        return;
      }

      this.FormEventMg.trigger('update_validate', { id, params });
    });

    this.roleId = LocalStorageService.getRoleId(); // ロールID

    // buildeeとの契約無しの状態で、URL直叩き遷移防止処理
    const contractFlag = SessionStorageService.getContractFlag();
    if (
      contractFlag === CONTRACT_FLAG.noContract.id &&
      isTargetRoleId([USER_ROLES.branch, USER_ROLES.manager], this.roleId)
    ) {
      return window.location.replace('/error-permission');
    }

    // 元請ユーザ以外の場合、権限エラー画面に遷移する
    if (!isPrimaryCompanyRoleId) {
      return window.location.replace('/error-permission');
    }
  }

  async onLoad() {
    const stateValue = _.cloneDeep(this.state);
    const company_id = Number(SessionStorageService.getCompanyId());
    let jvFlag = false;

    // 現場CCUS連携情報タブの状態を取得
    await FieldCcusApi.checkPermissionFieldTreeCooperationWithCcus(
      this.state.fieldId
    ).then(response => {
      if (!response.result) {
        return;
      }

      stateValue.disabledCooperationCcus = !_.get(
        response,
        'data.entities.enable_ccus_flag'
      );
    });

    // JV情報を取得
    jvCompanyList(this.state.fieldId)
      .then(response => {
        if (response.result) {
          const main_company = response.data.entities.company[0];
          if (company_id !== main_company.company_id) {
            jvFlag = true;
          }
          stateValue.jvFlag = jvFlag;
          stateValue.params.company_branch_id = main_company.company_branch_id;

          const Jvlist = _.slice(response.data.entities.company, 1);

          _.each(Jvlist, jv => {
            stateValue.jvsParams.push({
              company_id: jv.company_id,
              company_name: jv.name,
              company_branch_id: jv.company_branch_id,
              company_branch_name: jv.company_branch_name,
              address: jv.address,
            });
          });
        }
        this.setState(stateValue);
      })
      .catch(error => {
        stateValue.informationModal.show = true;
        stateValue.informationModal.message = extractErrorMessage(error);
      });
  }

  componentDidMount() {
    this.onLoad();

    this.FormEventMg.addEventListener('update_validate', this.eachValidate);
  }

  setEditingFlag = flag => {
    this.setState({ editingFlag: flag });
  };

  componentWillUnmount() {
    this.FormEventMg.deleteEvent('change_value');
    this.FormEventMg.deleteEvent('update_validate');
  }

  /***
   * @description 各バリデーション系メソッドに遷移させ、エラーメッセージをセット
   * @param {object} values id: 入力フォームID, params: 全入力パラメータ
   */
  eachValidate = values => {
    const { id, params } = values;

    let result = validationByJoi(FieldScheme, params);
    const _validate = _.cloneDeep(this.state.validate);
    result = this.checkValidate(result, params);

    if (result.status === false && !_.isEmpty(result.messages[id])) {
      // 入力項目に誤りがある場合、エラーメッセージをセット
      _validate[id] = result.messages[id];
    } else {
      // 正常に入力している場合、エラーメッセージを削除
      delete _validate[id];
    }

    this.setState({ validate: _validate });
  };

  /***
   * @description 詳細な相関バリデーションチェック
   * @param {object} result Joiで取得したエラーメッセージ
   * @param {object} params 全入力パラメータ
   * @returns バリデーションチェック後のエラーメッセージオブジェクト
   */
  checkValidate = (result, params) => {
    result.messages = result.messages || {};

    if (params.jvs) {
      result.messages.jvs = result.messages.jvs || {};

      let branch_ids = _.map(
        params.jvs,
        jv => `${jv.company_id}-${jv.company_branch_id}`
      );

      _.each(branch_ids, (id, i) => {
        if (branch_ids.indexOf(id) !== i) {
          result.messages.jvs[i] = messageUtility.getMessage('E-000-019');
          result.status = false;
        }
      });

      if (_.isEmpty(result.messages.jvs)) {
        delete result.messages.jvs;
      }
    }

    return result;
  };

  setInformationModal = informationModal => {
    this.setState({ informationModal });
  };

  openCloseEditModal = () => {
    if (this.state.editingFlag) {
      dialogLoader.show(CloseEditView, {
        title: messageUtility.getMessage('I-001-503'),
        message: messageUtility.getMessage('I-001-504'),
        confirmButtonName: messageUtility.getMessage('I-000-181'),
        multilineText: true,
        callback: {
          close: () => {
            dialogLoader.close();
          },
          clear: () => {
            _masterSlideLoader.show('Fields', {
              state: {},
            });
            dialogLoader.close();
          },
        },
      });
    } else {
      _masterSlideLoader.show('Fields', {
        state: {},
      });
    }
  };

  closeDialog = () => {
    this.setState({
      informationModal: {
        show: false,
        message: '',
      },
    });
  };

  render() {
    const { fieldId, validate } = this.state;

    return (
      <div id='slide-view' className='contruction-slide-view show'>
        <Helmet>
          <title>{HEADER_CONSTANTS.title} - 現場詳細</title>
        </Helmet>

        <div className='contruction-slide-view__view'>
          <MasterPageHeader
            title='現場詳細'
            onClose={this.events.modal.openCloseEditModal}
          />

          <div className='contruction-slide-view__body contruction-slide-view__body--height-fix'>
            <div className='contruction-detail-view'>
              <div className='contruction-detail-view__sidemenu'>
                <FieldNaviList
                  fieldId={fieldId}
                  selectedTab='FieldJvSetting'
                  disabledCooperationCcus={this.state.disabledCooperationCcus}
                  showJvSettingTab={true}
                  isEditable={this.state.editingFlag}
                  closeEditingDialog={{
                    title: messageUtility.getMessage('I-001-503'),
                    message: messageUtility.getMessage('I-001-504'),
                    confirmButtonName: messageUtility.getMessage('I-000-181'),
                    multilineText: true,
                  }}
                />
              </div>

              <div className='contruction-detail-view__body contruction-detail-view__body--fixed'>
                <div className='contruction-detail-view__header'>
                  <button
                    className='btn-text--sm'
                    onClick={this.events.modal.openCloseEditModal}
                  >
                    <i className='i-back--blue i-2x vertical-middle' />
                    現場一覧
                  </button>
                </div>

                <div className='contruction-detail-view__bar' />

                {/* ここからフォーム */}
                <div className='contruction-detail-view__main contruction-detail-view__main--fixed contruction-detail-view__main--fix'>
                  <FormList>
                    <Form EventMg={this.FormEventMg} submit={this.submit}>
                      <FormListSubHeader
                        title='JV構成会社'
                        noMarginTop={true}
                      />
                      {/* JV 追加フォーム */}
                      <JV
                        id='jvs'
                        validate={validate.jvs}
                        fieldId={this.state.fieldId}
                        companyBranchId={this.state.params.company_branch_id}
                        setEditingFlag={this.setEditingFlag}
                        jvsParams={
                          _.isEmpty(this.state.jvsParams)
                            ? null
                            : this.state.jvsParams
                        }
                        defaultValue={
                          _.isEmpty(this.state.jvsParams)
                            ? null
                            : this.state.jvsParams
                        }
                        setInformationModal={this.setInformationModal.bind(
                          this
                        )}
                        disabled={this.state.jvFlag}
                      />
                    </Form>
                  </FormList>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Information
          visible={this.state.informationModal.show}
          buttonText={messageUtility.getMessage('I-000-008')}
          title={messageUtility.getMessage('I-001-502')}
          message={this.state.informationModal.message}
          multilineText={true}
          onClose={() => this.closeDialog()}
        />
      </div>
    );
  }
}

/**
 * @description JVの追加フォーム
 */
export class JV extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      jvsList: [{}],
      showConfirm: false,
      confirmType: null,
      editingFlag: false,
    };

    this.stateFlg = false;
    this.companyId = parseInt(sessionStorage.getItem('company_id'), 10); // 元請ID
    this.noValueJvFlg = false;
  }

  componentDidUpdate(prevProps) {
    if (!_.isUndefined(this.props.defaultValue) && this.stateFlg !== true) {
      // 1度だけデフォルトの値をstateに設定
      this.stateFlg = true;

      this.setState({
        jvsList: _.defaultTo(this.props.defaultValue, this.props.jvsParams),
      });
      return;
    }

    if (
      _.isNull(this.state.jvsList) &&
      prevProps.defaultValue !== this.props.defaultValue
    ) {
      this.setState({
        jvsList: this.props.defaultValue,
      });
    }
  }

  /**
   * @description JV会社を設定
   * @param {string} item 選択JV会社
   * @param {int} index JVコンポーネント要素番号
   */
  setJvCompany(item, index) {
    let jvsList = [...this.state.jvsList];
    if (item.id === jvsList[index].company_id) {
      // 同じJV会社を選択した場合
      return false;
    }
    jvsList[index].company_id = item.id;
    jvsList[index].company_name = item.name;
    jvsList[index].companyBranches = item.company_branches;
    if (_.has(jvsList[index], 'company_branch_id')) {
      // JV支店を選択した状態でJV会社を変更した場合、
      // 支店を未選択状態にするため、JV支店情報を削除
      delete jvsList[index].company_branch_id;
      delete jvsList[index].company_branch_name;
    }
    this.setState({ jvsList });
    return true;
  }

  clearJvCompany(item, index) {
    console.log('clear:', item, index);
    let jvsList = [...this.state.jvsList];
    delete jvsList[index];
    this.setState({ jvsList });
  }

  /**
   * @description JV支店を設定
   * @param {string} item 選択JV支店
   * @param {int} index JVコンポーネント要素番号
   */
  setJvCompanyBranch(item, index) {
    let jvsList = [...this.state.jvsList];
    jvsList[index].company_branch_id = item.id;
    jvsList[index].company_branch_name = item.name;
    jvsList[index].address = item.address;
    this.setState({ jvsList });
  }

  /**
   * @description JVコンポーネント追加
   * @param {object} newProps 新しいprops
   */
  addJvForm(newProps) {
    let jvsList = !_.isEmpty(this.state.jvsList) ? [...this.state.jvsList] : [];
    jvsList.push({ onRegister: true });
    this.setState({ jvsList, editingFlag: true }, () => {
      newProps.onUpdate(this.state.jvsList);
    });
    this.props.setEditingFlag(true);
  }

  handleDelete(jv) {
    this.setState({ showConfirm: true, confirmTypeId: 0, targetJv: jv });
  }

  handleRegist(jv) {
    this.setState({ showConfirm: true, confirmTypeId: 1, targetJv: jv });
  }

  closeDialog() {
    this.setState({ showConfirm: false, confirmTypeId: null });
  }

  /**
   * @description 対象JV会社の行を削除
   * @param {int} index JVコンポーネント要素番号
   * @param {object} newProps 新しいprops
   */
  removeJv(index, newProps) {
    let jvsList = [...this.state.jvsList];
    jvsList.splice(index, 1);
    this.setState({ jvsList, editingFlag: false }, () => {
      newProps.onUpdate(this.state.jvsList);
    });
    this.props.setEditingFlag(false);
  }

  /**
   * @description JV会社を登録または削除
   * @param {int} jv JVコンポーネント要素番号
   */
  registOrDeleteJv(jv) {
    const params = {
      company_id: jv.company_id,
      company_branch_id: jv.company_branch_id,
    };

    switch (this.state.confirmTypeId) {
      case JV_ACTION.delete.id:
        CompaniesFieldsService.deleteJV(this.props.fieldId, params)
          .then(response => {
            if (!response.result) {
              this.props.setInformationModal({
                show: true,
                message: response.message,
              });
              return;
            }
            document.location.reload();
          })
          .catch(error => {
            this.props.setInformationModal({
              show: true,
              message: extractErrorMessage(error),
            });
          })
          .finally(this.setState({ showConfirm: false }));
        break;
      case JV_ACTION.regist.id:
        CompaniesFieldsService.createJV(this.props.fieldId, params)
          .then(response => {
            if (!response.result) {
              this.props.setInformationModal({
                show: true,
                message: response.message,
              });
              return;
            }
            document.location.reload();
          })
          .catch(error => {
            this.props.setInformationModal({
              show: true,
              message: extractErrorMessage(error),
            });
          })
          .finally(this.setState({ showConfirm: false }));
        break;

      default:
        break;
    }
  }

  /**
   * @description JV会社が設定されたか確認
   * @param {int} index JVコンポーネント要素番号
   */
  getHaveCompanyFlg(index) {
    if (_.isEmpty(this.state.jvsList)) {
      return false;
    }
    const jv = this.state.jvsList[index];
    let haveCompany = false;
    if (!_.isEmpty(jv)) {
      if (!_.isNull(jv.company_id) && !_.isUndefined(jv.company_id)) {
        haveCompany = true;
      }
    }
    return haveCompany;
  }

  render() {
    const { jvsList } = this.state;
    const jvsSize = jvsList ? jvsList.length : 0;
    const getValidate = index => {
      let validate = '';
      if (_.isObject(this.props.validate)) {
        let obj = this.props.validate[index];
        if (!_.isUndefined(obj)) {
          validate = _.isString(obj)
            ? obj
            : '元請会社と支店を選択してください。';
        }
      }
      return validate;
    };

    this.noValueJvFlg = _.some(jvsList, jv => {
      return !(_.has(jv, 'company_id') && _.has(jv, 'company_branch_id'));
    });

    return (
      <React.Fragment>
        <FormItem
          {...this.props}
          id='jvs'
          defaultValue={_.defaultTo(this.props.jvsParams, [{}])}
        >
          {newProps => (
            <FormListItem label='JV設定'>
              <FormElement className='form-list__element FieldJvSetting'>
                {
                  <FormListItem
                    className={`form-list__item FieldJvSetting__description ${
                      this.props.disabled ? 'hide' : ''
                    }`}
                  >
                    <Description type='primary'>
                      JV構成会社に追加したい会社名を入力して検索してください。
                      <br />
                      Buildeeを元請としてご利用中でない会社の場合、検索結果に表示されません。
                      <br />
                      追加したい会社が、Buildeeをご利用中でない場合は、JV構成会社に追加したい会社様からサブJVとしてのBuildee利用申込が必要となります。
                      <br />
                      <a
                        className='clickable-text'
                        data-testid='worker_insurance_manual_download_link'
                        href='https://service.buildee.jp/flow/prime/'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        こちら
                      </a>
                      からお申込みが可能です。
                    </Description>
                  </FormListItem>
                }
                {_.map(jvsList, (jv, index) => {
                  const _enable = this.getHaveCompanyFlg(index);
                  return (
                    <FormListItem key={index} isBorder={index !== jvsSize - 1}>
                      <FormElementContainer>
                        <FormElement validate={getValidate(index)}>
                          <FormElementContainerItem>
                            <div className='FieldJvSetting__company-name'>
                              <FormElementContainerItemLabel>
                                会社名
                              </FormElementContainerItemLabel>
                              {jv.onRegister ? (
                                <JvCompanyDropdown
                                  companyBranchId={this.props.companyBranchId}
                                  id={jv.company_id}
                                  value={jv.company_name}
                                  companyBranches={jv.companyBranches}
                                  disabled={this.props.disabled}
                                  onUpdate={item => {
                                    const updateFlg = this.setJvCompany(
                                      item,
                                      index
                                    );
                                    if (updateFlg === true) {
                                      newProps.onUpdate(this.state.jvsList);
                                    }
                                  }}
                                  onClear={item => {
                                    this.clearJvCompany(item, index);
                                  }}
                                  index={index}
                                />
                              ) : (
                                <div>{jv.company_name}</div>
                              )}
                            </div>
                            {(jv.companyBranches || jv.company_branch_name) && (
                              <div className='FieldJvSetting__company-branch-name'>
                                <FormElementContainerItemLabel isSpace={true}>
                                  支店名
                                </FormElementContainerItemLabel>
                                {jv.onRegister ? (
                                  <JvCompanyBranchDropdown
                                    id={jv.company_branch_id}
                                    value={jv.company_branch_name}
                                    companyBranches={jv.companyBranches}
                                    companyFlg={
                                      jv.company_id === this.companyId
                                    }
                                    companyBranchId={this.props.companyBranchId}
                                    enable={_enable}
                                    detailFlg={this.props.detailFlg}
                                    onUpdate={item => {
                                      this.setJvCompanyBranch(item, index);
                                      newProps.onUpdate(this.state.jvsList);
                                    }}
                                    index={index}
                                  />
                                ) : (
                                  <div>{jv.company_branch_name}</div>
                                )}
                              </div>
                            )}

                            {jv.onRegister ? (
                              <div className='FieldJvSetting__on-regist-btn'>
                                <button
                                  className={`btn--sm--effective ${
                                    !jv.company_branch_name ||
                                    getValidate(index)
                                      ? 'btn--disabled'
                                      : ''
                                  }`}
                                  onClick={() => {
                                    this.handleRegist(jv);
                                  }}
                                  disabled={
                                    this.noValueJvFlg || getValidate(index)
                                  }
                                >
                                  登録
                                </button>
                                <button
                                  className='btn--sm--negative'
                                  onClick={() => {
                                    this.removeJv(index, newProps);
                                  }}
                                >
                                  削除
                                </button>
                              </div>
                            ) : (
                              <div
                                className={`FieldJvSetting__delete-btn ${
                                  this.state.editingFlag || this.props.disabled
                                    ? 'hide'
                                    : ''
                                }`}
                              >
                                <button
                                  className={'btn--sm--negative'}
                                  onClick={() => {
                                    this.handleDelete(jv);
                                  }}
                                >
                                  削除
                                </button>
                              </div>
                            )}
                          </FormElementContainerItem>
                        </FormElement>
                      </FormElementContainer>
                    </FormListItem>
                  );
                })}
                {!this.props.disabled && (
                  <FormListItem isBorder={false}>
                    <FormElementContainer>
                      <FormElementContainerItem>
                        <button
                          disabled={this.state.editingFlag}
                          className={`btn-fixedsize--sub ${
                            this.state.editingFlag
                              ? 'btn-fixedsize--disabled'
                              : ''
                          }`}
                          onClick={() => {
                            if (!this.state.editingFlag) {
                              this.addJvForm(newProps);
                            }
                          }}
                        >
                          追加する
                        </button>
                      </FormElementContainerItem>
                    </FormElementContainer>
                  </FormListItem>
                )}
              </FormElement>
            </FormListItem>
          )}
        </FormItem>
        {this.state.targetJv && (
          <Confirmation
            visible={this.state.showConfirm}
            title={
              this.state.confirmTypeId == JV_ACTION.delete.id
                ? messageUtility.getMessage('I-001-500')
                : messageUtility.getMessage('I-001-501')
            }
            confirmButtonText={
              this.state.confirmTypeId == JV_ACTION.delete.id
                ? JV_ACTION.delete.name
                : JV_ACTION.regist.name
            }
            cancelButtonText={messageUtility.getMessage('I-000-008')}
            message={
              this.state.confirmTypeId == JV_ACTION.delete.id
                ? messageUtility.getMessage(
                    'I-001-458',
                    this.state.targetJv.company_name,
                    this.state.targetJv.company_branch_name,
                    this.state.targetJv.address
                  )
                : messageUtility.getMessage(
                    'I-001-459',
                    this.state.targetJv.company_name,
                    this.state.targetJv.company_branch_name,
                    this.state.targetJv.address
                  )
            }
            isNegative={
              this.state.confirmTypeId == JV_ACTION.delete.id ? true : false
            }
            onCancel={() => this.closeDialog()}
            onConfirm={() => this.registOrDeleteJv(this.state.targetJv)}
            negativeButtonClassName={
              this.state.confirmTypeId == JV_ACTION.delete.id
                ? 'btn--negative'
                : ''
            }
            multilineText={true}
          />
        )}
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/FieldJvSetting.jsx